import { SimState, Tile, BuildingType, BUILDING_SIZE } from './types';

/**
 * Single-source helpers for plot-level queries: tile lookup, building
 * placement validity, neighbor traversal.
 */
export const PlotSystem = {
  in(state: SimState, c: number, r: number): boolean {
    const p = state.plot;
    return c >= 0 && r >= 0 && c < p.width && r < p.height;
  },

  tile(state: SimState, c: number, r: number): Tile | null {
    if (!this.in(state, c, r)) return null;
    return state.plot.tiles[r][c];
  },

  /** Tile is "cleared" — no wild thing, no building, suitable to till/build. */
  isCleared(state: SimState, c: number, r: number): boolean {
    const t = this.tile(state, c, r);
    if (!t) return false;
    return !t.thing && !t.bld && (t.surf === 'grass' || t.surf === 'field');
  },

  /**
   * Can a new building of `type` be placed with its SW-corner anchor at (c, r)?
   * Every tile in the footprint must be in-bounds, empty, and not on the road.
   */
  canBuildAt(state: SimState, c: number, r: number, type: BuildingType): boolean {
    const { w, d } = BUILDING_SIZE[type];
    for (let dr = 0; dr < d; dr++) {
      for (let dc = 0; dc < w; dc++) {
        const t = this.tile(state, c + dc, r + dr);
        if (!t) return false;
        if (t.bld) return false;
        if (t.thing) return false;
        if (t.crop) return false;
        if (t.surf === 'road') return false;
      }
    }
    return true;
  },

  /** Resolve a tile's building anchor (the SW corner of its footprint, or
   *  itself if 1×1 / no explicit anchor). Returns null if no building here. */
  buildingAnchor(state: SimState, c: number, r: number): { c: number; r: number; tile: Tile } | null {
    const t = this.tile(state, c, r);
    if (!t || !t.bld) return null;
    const a = t.bldAnchor ?? { c, r };
    const at = this.tile(state, a.c, a.r);
    if (!at) return null;
    return { c: a.c, r: a.r, tile: at };
  },

  /** True when (c, r) is the anchor tile of its building (i.e. SW corner). */
  isAnchor(state: SimState, c: number, r: number): boolean {
    const t = this.tile(state, c, r);
    if (!t || !t.bld) return false;
    if (!t.bldAnchor) return true;
    return t.bldAnchor.c === c && t.bldAnchor.r === r;
  },

  /** Yield each tile in the plot via callback. */
  forEachTile(state: SimState, fn: (t: Tile, c: number, r: number) => void) {
    const p = state.plot;
    for (let r = 0; r < p.height; r++) {
      for (let c = 0; c < p.width; c++) {
        fn(p.tiles[r][c], c, r);
      }
    }
  },

  /** Find the (col,row) of the first tile matching a predicate, or null. */
  findTile(state: SimState, pred: (t: Tile, c: number, r: number) => boolean): { c: number; r: number } | null {
    const p = state.plot;
    for (let r = 0; r < p.height; r++) {
      for (let c = 0; c < p.width; c++) {
        if (pred(p.tiles[r][c], c, r)) return { c, r };
      }
    }
    return null;
  },

  /** Anchor tiles for every building of `type` (one entry per building). */
  buildingsOfType(state: SimState, type: BuildingType): { c: number; r: number; tile: Tile }[] {
    const out: { c: number; r: number; tile: Tile }[] = [];
    this.forEachTile(state, (t, c, r) => {
      if (t.bld === type && this.isAnchor(state, c, r)) {
        out.push({ c, r, tile: t });
      }
    });
    return out;
  },

  /** Manhattan distance between two tile coords. */
  dist(c1: number, r1: number, c2: number, r2: number) {
    return Math.abs(c1 - c2) + Math.abs(r1 - r2);
  },
};
